import DeleteAdornment from '../core/DeleteAdornment';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import DoneIcon from '@material-ui/icons/Done';
import ErrorIcon from '@material-ui/icons/ErrorOutlined';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles as styles } from './style/gstheme';
import { sanitiseText, createLengthWarning2 } from './utils/utils';

class TextConfirmationField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            originalText: props.text,
            text: props.text,
            error: false,
            isValid: false,
            isDirty: false
        };
    }

    static getDerivedStateFromProps(props, state) {
        if (props.text !== state.originalText) {
            return {
                text: props.text,
                originalText: props.text
            };
        }
        return null;
    }

    checkValidity() {
        if (this.state.text.trim() === "" || this.state.text.trim() === this.state.originalText) {
            this.setState({ error: false });
            return;
        }
        let validity = this.checkValid(this.state.text.trim());
        this.setState({ error: !validity.isValid });
    }

    checkValid(text) {
        let isValid = true;
        try {
            new RegExp(text);
        } catch (e) {
            isValid = false;
        }
        return { isValid };
    }

    handleChange = (event) => {
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ text: cleanText, isDirty: true }, this.checkValidity);
    };

    reset = () => this.setState({
        text: this.state.originalText,
        error: false,
        isDirty: false
    });

    handleUpdate = () => {
        this.setState({ isDirty: false });
        this.props.onUpdate(this.state.text.trim());
    };

    handleDelete = () => this.props.onDelete(this.state.text);

    render() {

        const { classes, isReadOnly, label, margin, dataTest, compact, maxLength, ariaLabel } = this.props;
        const { text, originalText, error, isDirty } = this.state;

        let icon = null;
        const isEdited = text.toString().trim() !== originalText.toString().trim();
        if (error) {
            icon =
                <InputAdornment position="end">
                    <IconButton aria-label="Invalid value" title="The value is invalid" className={classes.smallIconButton}>
                        <ErrorIcon data-test="error" fontSize="small" />
                    </IconButton>
                </InputAdornment>;
        }
        else if (!isReadOnly && text.toString().trim().length > 0 && isEdited) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="Confirm change" onClick={this.handleUpdate}>
                        <IconButton aria-label="Confirm change" className={classes.smallIconButton}>
                            <DoneIcon data-test="done" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }
        else if (!isReadOnly && !isEdited && text.toString().trim().length > 0) {
            icon = <DeleteAdornment dataTest={dataTest} fontSize="small" onClick={this.handleDelete} />;
        }

        return (
            <ClickAwayListener onClickAway={text !== this.state.originalText || error ? this.reset : () => { }}>
                <div data-test={dataTest + "-container"}>
                    <TextField
                        value={text}
                        variant="outlined"
                        style={compact ? { marginTop: '0px' } : {}}
                        margin={margin}
                        onChange={this.handleChange}
                        error={error}
                        label={label}
                        fullWidth
                        onKeyPress={(ev) => {
                            if (!isReadOnly && text.toString().trim().length > 0 && ev.key === 'Enter') {
                                this.handleUpdate();
                                ev.preventDefault();
                                ev.stopPropagation();
                            }
                        }}
                        InputProps={{
                            className: classes.inputLabel,
                            endAdornment: icon,
                            inputProps: {
                                "data-test": dataTest,
                                "aria-label": ariaLabel,
                                readOnly: isReadOnly,
                                maxLength: this.props.maxLength
                            }
                        }}
                        helperText={createLengthWarning2(text, maxLength, isDirty)}
                    />
                </div>
            </ClickAwayListener>
        );
    }
}

TextConfirmationField.defaultProps = {
    isReadOnly: false,
    margin: "dense",
    compact: true,
    maxLength: 100
};

TextConfirmationField.propTypes = {
    dataTest: PropTypes.string,
    ariaLabel: PropTypes.string,
    isReadOnly: PropTypes.bool,
    compact: PropTypes.bool,
    maxLength: PropTypes.number,
    margin: PropTypes.oneOf(['normal', 'dense']),
    onUpdate: PropTypes.func,
    text: PropTypes.string,
    label: PropTypes.string,
    classes: PropTypes.object
};

export default withStyles(styles)(TextConfirmationField);